import type { Locale } from '../index'

/**
 * Contact page copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * zh-Hant (Traditional — Taiwan / HK) and zh-Hans (Simplified — Mainland China)
 * are distinct, idiomatic translations — never mechanically converted from one
 * to the other. Brand names, the legal entity, emails/phones/URLs, select
 * option `value` codes and bare numbers are intentionally left unkeyed.
 */
export const contact: Record<Locale, Record<string, string>> = {
  en: {
    'contact.head.title': 'Contact — Geo4Logistics',
    'contact.head.description':
      'Reach the Geo4Logistics team — support, accounts, billing and general enquiries. Bilingual coordinators and offices across Asia and the Americas.',

    'contact.hero.title': 'Contact us',
    'contact.hero.lead':
      "Questions about a shipment, your account or a bill — or just want to talk to a person? Send us a message and we'll get back to you, usually within 1–2 business days.",

    'contact.form.eyebrow': 'Send a message',
    'contact.form.title': 'How can we help?',
    'contact.pricing.title': 'Looking for pricing?',
    'contact.pricing.before': 'For a freight quote, use the',
    'contact.pricing.link': 'Get a quote',
    'contact.pricing.after': 'form — it asks for the mode, route and cargo so we can price it.',

    'contact.form.name.label': 'Full name *',
    'contact.form.name.placeholder': 'Your name',
    'contact.form.company.label': 'Company',
    'contact.form.company.placeholder': 'Company name',
    'contact.form.email.label': 'Work email *',
    'contact.form.email.placeholder': 'you@company.com',
    'contact.form.phone.label': 'Phone',
    'contact.form.phone.placeholder': '+1 / +886 …',
    'contact.form.topic.label': "What's this about?",
    'contact.form.topic.general': 'General enquiry',
    'contact.form.topic.shipment': 'An existing shipment',
    'contact.form.topic.account': 'Account & customer portal',
    'contact.form.topic.billing': 'Invoices & billing',
    'contact.form.topic.partnership': 'Partnership / carrier',
    'contact.form.topic.careers': 'Careers',
    'contact.form.message.label': 'Message *',
    'contact.form.message.placeholder': 'Tell us how we can help…',
    'contact.form.submit': 'Send message',
    'contact.form.reply_note': 'We typically reply within 1–2 business days.',
    'contact.form.error': 'Please check the highlighted fields and try again.',
    'contact.form.success':
      "Thanks! Your message is in — we'll be in touch within 1–2 business days.",
    'contact.form.disabled':
      'Our contact form is unavailable right now — please email or call us using the details on the right.',

    'contact.method.chat.title': 'Live chat',
    'contact.method.chat.text': 'Fastest way to reach us.',
    'contact.method.chat.link': 'Start a chat →',
    'contact.method.email.title': 'Email',
    'contact.method.email.support': 'Support:',
    'contact.method.phone.title': 'Phone',
    'contact.method.phone.taipei': 'Taipei',
    'contact.method.phone.chicago': 'Chicago',
    'contact.method.hours.title': 'Support hours',
    'contact.method.hours.line1': 'Mon–Fri, follow-the-sun across our offices.',
    'contact.method.hours.line2': 'Avg. response under 2 hours.',
    'contact.method.self.title': 'Self-serve',
    'contact.method.self.before': 'Browse the',
    'contact.method.self.help': 'help centre',
    'contact.method.self.or': 'or',
    'contact.method.self.track': 'track a shipment',
    'contact.method.self.after': 'any time.',

    'contact.offices.eyebrow': 'Where we are',
    'contact.offices.title': 'Our offices',
    'contact.offices.lead':
      'Teams in Asia and the Americas, across the time zones where your cargo moves.',
    'contact.offices.0.city': 'Kaohsiung',
    'contact.offices.0.line1': 'No. 2 Hai Bian Rd, Lingya District',
    'contact.offices.0.line2': 'Kaohsiung, Taiwan',
    'contact.offices.0.tag': 'Headquarters',
    'contact.offices.1.city': 'Shanghai',
    'contact.offices.1.line1': 'Pudong New Area',
    'contact.offices.1.line2': 'Shanghai, China',
    'contact.offices.1.tag': 'Origin operations',
    'contact.offices.2.city': 'Chicago',
    'contact.offices.2.line1': 'W Jackson Blvd',
    'contact.offices.2.line2': 'Chicago, IL, USA',
    'contact.offices.2.tag': 'Destination & customs',

    'contact.careers.title': 'Work with us',
    'contact.careers.lead':
      "We're always glad to hear from forwarders, customs specialists and engineers who care about getting freight — and the software behind it — right.",
  },

  'zh-Hant': {
    'contact.head.title': '聯絡我們 — Geo4Logistics',
    'contact.head.description':
      '聯絡 Geo4Logistics 團隊 — 支援、帳戶、帳務與一般諮詢。我們備有雙語協調人員，並於亞洲與美洲設有辦公室。',

    'contact.hero.title': '聯絡我們',
    'contact.hero.lead':
      '對某筆貨件、您的帳戶或帳單有疑問，或只是想找專人聊聊？歡迎傳訊息給我們，我們通常會在一至兩個工作天內回覆。',

    'contact.form.eyebrow': '傳送訊息',
    'contact.form.title': '需要什麼協助嗎？',
    'contact.pricing.title': '想了解價格嗎？',
    'contact.pricing.before': '如需運費報價，請使用',
    'contact.pricing.link': '取得報價',
    'contact.pricing.after': '表單 — 填寫運輸方式、航線與貨物後，我們便能為您估價。',

    'contact.form.name.label': '姓名 *',
    'contact.form.name.placeholder': '您的姓名',
    'contact.form.company.label': '公司',
    'contact.form.company.placeholder': '公司名稱',
    'contact.form.email.label': '公司電子郵件 *',
    'contact.form.email.placeholder': 'you@company.com',
    'contact.form.phone.label': '電話',
    'contact.form.phone.placeholder': '+1 / +886 …',
    'contact.form.topic.label': '這次想諮詢的主旨是？',
    'contact.form.topic.general': '一般諮詢',
    'contact.form.topic.shipment': '既有貨件',
    'contact.form.topic.account': '帳戶與客戶平台',
    'contact.form.topic.billing': '發票與帳務',
    'contact.form.topic.partnership': '合作／承運業者',
    'contact.form.topic.careers': '人才招募',
    'contact.form.message.label': '訊息 *',
    'contact.form.message.placeholder': '請告訴我們能如何協助您…',
    'contact.form.submit': '送出訊息',
    'contact.form.reply_note': '我們通常會在一至兩個工作天內回覆。',
    'contact.form.error': '請檢查標示的欄位後再試一次。',
    'contact.form.success': '感謝您！您的訊息已送出，我們會於一至兩個工作天內與您聯繫。',
    'contact.form.disabled': '目前聯絡表單暫時無法使用 — 請透過右側的電子郵件或電話與我們聯繫。',

    'contact.method.chat.title': '線上客服',
    'contact.method.chat.text': '與我們聯繫最快的方式。',
    'contact.method.chat.link': '開始對話 →',
    'contact.method.email.title': '電子郵件',
    'contact.method.email.support': '支援：',
    'contact.method.phone.title': '電話',
    'contact.method.phone.taipei': '台北',
    'contact.method.phone.chicago': '芝加哥',
    'contact.method.hours.title': '支援時間',
    'contact.method.hours.line1': '週一至週五，各辦公室接力提供全日服務。',
    'contact.method.hours.line2': '平均回覆時間少於 2 小時。',
    'contact.method.self.title': '自助服務',
    'contact.method.self.before': '隨時可瀏覽',
    'contact.method.self.help': '支援中心',
    'contact.method.self.or': '或',
    'contact.method.self.track': '查詢貨態',
    'contact.method.self.after': '。',

    'contact.offices.eyebrow': '據點位置',
    'contact.offices.title': '我們的辦公室',
    'contact.offices.lead': '團隊遍布亞洲與美洲，橫跨貨物流動的各個時區。',
    'contact.offices.0.city': '高雄',
    'contact.offices.0.line1': '苓雅區海邊路 2 號',
    'contact.offices.0.line2': '台灣高雄',
    'contact.offices.0.tag': '總部',
    'contact.offices.1.city': '上海',
    'contact.offices.1.line1': '浦東新區',
    'contact.offices.1.line2': '中國上海',
    'contact.offices.1.tag': '起運作業',
    'contact.offices.2.city': '芝加哥',
    'contact.offices.2.line1': 'W Jackson Blvd',
    'contact.offices.2.line2': '美國伊利諾州芝加哥',
    'contact.offices.2.tag': '目的地與報關',

    'contact.careers.title': '與我們共事',
    'contact.careers.lead':
      '我們始終樂於聽到貨運代理、報關專家與工程師的聲音 — 只要您一樣在乎把貨運，以及背後的軟體，都做到位。',
  },

  'zh-Hans': {
    'contact.head.title': '联系我们 — Geo4Logistics',
    'contact.head.description':
      '联系 Geo4Logistics 团队 — 支持、账户、账务与一般咨询。我们配备双语协调人员，并在亚洲与美洲设有办公室。',

    'contact.hero.title': '联系我们',
    'contact.hero.lead':
      '对某笔货件、您的账户或账单有疑问，或只是想找专人聊聊？欢迎给我们发消息，我们通常会在一到两个工作日内回复。',

    'contact.form.eyebrow': '发送消息',
    'contact.form.title': '需要什么帮助吗？',
    'contact.pricing.title': '想了解价格吗？',
    'contact.pricing.before': '如需运费报价，请使用',
    'contact.pricing.link': '获取报价',
    'contact.pricing.after': '表单 — 填写运输方式、航线与货物后，我们即可为您估价。',

    'contact.form.name.label': '姓名 *',
    'contact.form.name.placeholder': '您的姓名',
    'contact.form.company.label': '公司',
    'contact.form.company.placeholder': '公司名称',
    'contact.form.email.label': '公司电子邮箱 *',
    'contact.form.email.placeholder': 'you@company.com',
    'contact.form.phone.label': '电话',
    'contact.form.phone.placeholder': '+1 / +886 …',
    'contact.form.topic.label': '这次想咨询的主题是？',
    'contact.form.topic.general': '一般咨询',
    'contact.form.topic.shipment': '已有货件',
    'contact.form.topic.account': '账户与客户平台',
    'contact.form.topic.billing': '发票与账务',
    'contact.form.topic.partnership': '合作／承运商',
    'contact.form.topic.careers': '招贤纳士',
    'contact.form.message.label': '消息 *',
    'contact.form.message.placeholder': '请告诉我们能如何帮助您…',
    'contact.form.submit': '发送消息',
    'contact.form.reply_note': '我们通常会在一到两个工作日内回复。',
    'contact.form.error': '请检查标示的字段后重试。',
    'contact.form.success': '感谢您！您的消息已发送，我们会于一到两个工作日内与您联系。',
    'contact.form.disabled': '目前联系表单暂时无法使用 — 请通过右侧的电子邮箱或电话与我们联系。',

    'contact.method.chat.title': '在线客服',
    'contact.method.chat.text': '与我们联系最快的方式。',
    'contact.method.chat.link': '开始对话 →',
    'contact.method.email.title': '电子邮箱',
    'contact.method.email.support': '支持：',
    'contact.method.phone.title': '电话',
    'contact.method.phone.taipei': '台北',
    'contact.method.phone.chicago': '芝加哥',
    'contact.method.hours.title': '支持时间',
    'contact.method.hours.line1': '周一至周五，各办公室接力提供全天候服务。',
    'contact.method.hours.line2': '平均回复时间少于 2 小时。',
    'contact.method.self.title': '自助服务',
    'contact.method.self.before': '随时可浏览',
    'contact.method.self.help': '帮助中心',
    'contact.method.self.or': '或',
    'contact.method.self.track': '追踪货物',
    'contact.method.self.after': '。',

    'contact.offices.eyebrow': '据点位置',
    'contact.offices.title': '我们的办公室',
    'contact.offices.lead': '团队遍布亚洲与美洲，横跨货物流动的各个时区。',
    'contact.offices.0.city': '高雄',
    'contact.offices.0.line1': '苓雅区海边路 2 号',
    'contact.offices.0.line2': '中国台湾高雄',
    'contact.offices.0.tag': '总部',
    'contact.offices.1.city': '上海',
    'contact.offices.1.line1': '浦东新区',
    'contact.offices.1.line2': '中国上海',
    'contact.offices.1.tag': '起运作业',
    'contact.offices.2.city': '芝加哥',
    'contact.offices.2.line1': 'W Jackson Blvd',
    'contact.offices.2.line2': '美国伊利诺伊州芝加哥',
    'contact.offices.2.tag': '目的地与报关',

    'contact.careers.title': '与我们共事',
    'contact.careers.lead':
      '我们始终乐于听到货运代理、报关专家与工程师的声音 — 只要您同样在乎把货运，以及背后的软件，都做到位。',
  },
}
